import React, { useState, useEffect, useCallback, useRef } from 'react';
// eslint-disable-next-line no-restricted-imports
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDomain } from '../../features/domains/hooks/useDomains';
import { UrlDiscovery } from '../../features/domains/components';
import { useDeleteFile } from '../../features/files/hooks/useFiles';
import { api, accessDBApi } from '../../services/api';
import { fileService } from '../../services/fileService';
import { getDomainDetailPath } from '../../config/routes';
import { usePageTitle } from '../../hooks/usePageTitle';
import { FILE_UPLOAD_LIMITS } from '../../config/constants';
import { showToast } from '../../stores/uiStore';

const SUPPORTED_EXTENSIONS = ['.pdf', '.txt'];

interface FileUploadItem {
  file: File;
  fileId?: string;
  s3Key?: string;
  progress: number; // S3 upload %
  error?: string;
  rejectedReason?: string;
  done: boolean;
}

export const UrlDiscoveryPage: React.FC = () => {
  usePageTitle('学習ページ管理');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: domainData, refetch: refetchDomain } = useDomain(id!);
  const domain = domainData?.data;
  const serverFiles = domain?.crawl_files ?? [];
  const [optimisticFiles, setOptimisticFiles] = useState<Array<{ url?: string; sync_status?: string; file_id?: string; error_message?: string; source_type?: string; original_filename?: string }>>([]);
  const crawlFiles = [...serverFiles, ...optimisticFiles.filter(o => !serverFiles.some(s => s.url === o.url))];
  const { mutate: deleteFile } = useDeleteFile(id!);

  const [activeTab, setActiveTab] = useState<'url' | 'file'>('url');
  const [addUrl, setAddUrl] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [confirmDeleteFile, setConfirmDeleteFile] = useState<{ label: string; file_id: string } | null>(null);

  // File tab state
  const [selectedFiles, setSelectedFiles] = useState<FileUploadItem[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isLearning, setIsLearning] = useState(false);
  const [learnStorageError, setLearnStorageError] = useState<string | null>(null);
  const [isRelearning, setIsRelearning] = useState(false);
  const [showRelearnConfirm, setShowRelearnConfirm] = useState(false);
  const [relearnUrls, setRelearnUrls] = useState<Array<{ url: string; source: string }>>([]);
  const [showRelearnSelect, setShowRelearnSelect] = useState(false);
  const [recrawlingFileId, setRecrawlingFileId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAddUrl = async () => {
    if (!addUrl.trim() || !id) return;
    setIsAdding(true);
    try {
      const trimmedUrl = addUrl.trim();
      let fullUrl = trimmedUrl.startsWith('http') ? trimmedUrl : 'https://' + trimmedUrl;
      // 末尾スラッシュ正規化（トップページ除く）
      try {
        const parsed = new URL(fullUrl);
        if (parsed.pathname !== '/' && fullUrl.endsWith('/')) {
          fullUrl = fullUrl.replace(/\/+$/, '');
        }
      } catch { /* invalid URL — そのまま通す */ }
      // ドメイン不一致チェック
      try {
        const inputHost = new URL(fullUrl).hostname;
        const domainRaw = domain?.domain ?? '';
        const domainHost = domainRaw.replace(/^https?:\/\//, '').split('/')[0];
        if (inputHost && domainHost && inputHost !== domainHost) {
          showToast.error(`このBOTは「${domainHost}」のURLのみ追加できます`);
          setIsAdding(false);
          return;
        }
      } catch {
        showToast.error('URLの形式が正しくありません');
        setIsAdding(false);
        return;
      }
      // 既存URL重複チェック
      const normalizeTrailingSlash = (u: string) => {
        try {
          const p = new URL(u);
          return p.pathname !== '/' && u.endsWith('/') ? u.replace(/\/+$/, '') : u;
        } catch { return u; }
      };
      const normalizedFullUrl = normalizeTrailingSlash(fullUrl);
      const existingFile = crawlFiles.find(f => {
        const existingUrl = normalizeTrailingSlash(f.url ?? '');
        return existingUrl === normalizedFullUrl;
      });
      if (existingFile) {
        if (existingFile.file_id) {
          showToast.info('このURLは登録済みです。再学習を開始します');
          setAddUrl('');
          setIsAdding(false);
          handleRecrawlUrl(fullUrl, existingFile.file_id);
        } else {
          showToast.info('このURLは既に学習中です');
          setIsAdding(false);
        }
        return;
      }
      await accessDBApi.post(`/v1/domains/${id}/crawl/batch`, { urls: [fullUrl] });
      setOptimisticFiles(prev => [...prev, { url: fullUrl, sync_status: 'pending', source_type: 'crawl' }]);
      setAddUrl('');
      await refetchDomain();
      // ポーリング開始（追加URLの学習完了まで）
      const pollId = setInterval(async () => {
        const res = await refetchDomain();
        const files = res.data?.data?.crawl_files ?? [];
        const target = files.find((f: any) => f.url === fullUrl);
        if (target && (target.sync_status === 'synced' || target.sync_status === 'error')) {
          clearInterval(pollId);
          setIsAdding(false);
        }
      }, 5000);
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'URLの追加に失敗しました';
      showToast.error(msg);
      setIsAdding(false);
    }
  };

  const handleConfirmDelete = () => {
    if (!confirmDeleteFile) return;
    deleteFile(confirmDeleteFile.file_id, {
      onSuccess: () => {
        setConfirmDeleteFile(null);
        refetchDomain();
      }
    });
  };

  const urlFiles = crawlFiles.filter(f => f.source_type !== 'upload');
  const uploadedFiles = crawlFiles.filter(f => f.source_type === 'upload');

  // File validation
  const validateFile = (file: File): string | null => {
    if (file.size > FILE_UPLOAD_LIMITS.maxSize) {
      return `${file.name}: ファイルサイズが10MBを超えています`;
    }
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!SUPPORTED_EXTENSIONS.includes(ext)) {
      return `${file.name}: 対応していないファイル形式です（PDF/.txtのみ）`;
    }
    return null;
  };

  const addFilesToQueue = useCallback((files: File[]) => {
    setValidationError(null);
    setLearnStorageError(null);
    const errors: string[] = [];
    const valid: FileUploadItem[] = [];
    for (const file of files) {
      const err = validateFile(file);
      if (err) {
        errors.push(err);
      } else {
        valid.push({ file, progress: 0, done: false });
      }
    }
    if (errors.length > 0) {
      setValidationError(errors.join(' / '));
    }
    if (valid.length > 0) {
      setSelectedFiles(prev => [...prev, ...valid]);
    }
  }, []);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    addFilesToQueue(files);
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  const handleDragLeave = () => setIsDragOver(false);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    addFilesToQueue(files);
  };

  const removeSelectedFile = (idx: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleLearnFiles = async () => {
    if (!id || selectedFiles.length === 0 || isLearning) return;
    setIsLearning(true);
    setLearnStorageError(null);

    const uploadedItems: { file_id: string; s3_key: string; content_type: string; filename: string; idx: number }[] = [];

    // Step 1: presign + S3 PUT for each file
    for (let i = 0; i < selectedFiles.length; i++) {
      const item = selectedFiles[i];
      if (item.done || item.error) continue;

      try {
        // Presign
        const presignRes = await api.post(`/domains/${id}/files/upload-url`, {
          filename: item.file.name,
          content_type: item.file.type || 'application/octet-stream',
          file_size: item.file.size,
        });
        const { file_id, upload_url, s3_key } = presignRes.data?.data ?? presignRes.data;

        // S3 PUT with progress
        await axios.put(upload_url, item.file, {
          headers: { 'Content-Type': item.file.type || 'application/octet-stream' },
          onUploadProgress: (ev) => {
            if (ev.total) {
              const pct = Math.round((ev.loaded / ev.total) * 100);
              setSelectedFiles(prev => prev.map((f, fi) => fi === i ? { ...f, progress: pct } : f));
            }
          },
        });

        uploadedItems.push({
          file_id,
          s3_key,
          content_type: item.file.type || (item.file.name.endsWith('.pdf') ? 'application/pdf' : 'text/plain'),
          filename: item.file.name,
          idx: i,
        });
      } catch (err: any) {
        const msg = err?.response?.data?.error?.message ?? 'アップロードに失敗しました';
        const status = err?.response?.status;
        if (status === 400 && /storage|ストレージ|quota/i.test(msg)) {
          setLearnStorageError(msg);
          setIsLearning(false);
          return;
        }
        setSelectedFiles(prev => prev.map((f, fi) => fi === i ? { ...f, error: msg } : f));
      }
    }

    if (uploadedItems.length === 0) {
      setIsLearning(false);
      return;
    }

    // Step 2: POST /files/learn
    try {
      const learnRes = await api.post(`/domains/${id}/files/learn`, {
        files: uploadedItems.map(u => ({
          file_id: u.file_id,
          s3_key: u.s3_key,
          content_type: u.content_type,
          filename: u.filename,
        })),
      });

      const resData = learnRes.data?.data ?? learnRes.data;
      const rejected: { file_id: string; reason: string }[] = resData?.rejected ?? [];

      // Mark rejected files
      if (rejected.length > 0) {
        setSelectedFiles(prev => prev.map((f, fi) => {
          const upItem = uploadedItems.find(u => u.idx === fi);
          if (!upItem) return f;
          const rej = rejected.find(r => r.file_id === upItem.file_id);
          return rej ? { ...f, rejectedReason: rej.reason } : { ...f, done: true };
        }));
      } else {
        setSelectedFiles(prev => prev.map((f, fi) => {
          const upItem = uploadedItems.find(u => u.idx === fi);
          return upItem ? { ...f, done: true } : f;
        }));
      }

      await refetchDomain();
      // Clear done files after refetch
      setSelectedFiles(prev => prev.filter(f => !f.done));
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.error?.message ?? '';
      if (status === 400 || status === 403) {
        if (/storage|ストレージ|quota|プラン/i.test(msg)) {
          setLearnStorageError(msg);
        }
      }
    } finally {
      setIsLearning(false);
    }
  };

  const handleRelearn = async () => {
    setShowRelearnConfirm(false);
    setIsRelearning(true);
    try {
      // 1. crawlファイルのみ削除（uploadは残す）
      const crawlOnly = crawlFiles.filter(f => f.source_type !== 'upload' && f.file_id);
      await Promise.allSettled(
        crawlOnly.map(f => fileService.deleteFile(id!, f.file_id!))
      );
      // 2. discover-urls再実行
      const res = await accessDBApi.post(`/v1/domains/${id}/discover-urls`, {});
      const rawUrls = res.data?.data?.urls ?? [];
      const urls = rawUrls.map((u: any) => typeof u === 'string' ? { url: u, source: 'crawl' } : u).filter(Boolean);

      if (urls.length <= 50) {
        // 小規模: 従来通り全自動
        const urlStrings = urls.map((u: any) => u.url || u).filter(Boolean);
        if (urlStrings.length > 0) {
          const BATCH_SIZE = 200;
          for (let i = 0; i < urlStrings.length; i += BATCH_SIZE) {
            await accessDBApi.post(`/v1/domains/${id}/crawl/batch`, { urls: urlStrings.slice(i, i + BATCH_SIZE) });
          }
        }
        // ポーリング開始（全ファイル完了まで）
        const pollId = setInterval(async () => {
          const pollRes = await refetchDomain();
          const files = pollRes.data?.data?.crawl_files ?? [];
          const urlOnly = files.filter((f: any) => f.source_type !== 'upload');
          const allDone = urlOnly.length > 0 && urlOnly.every((f: any) => f.sync_status === 'synced' || f.sync_status === 'error');
          if (allDone) {
            clearInterval(pollId);
            setIsRelearning(false);
          }
        }, 5000);
      } else {
        // 大量URL: 選択画面表示
        setRelearnUrls(urls);
        setShowRelearnSelect(true);
        setIsRelearning(false);
      }
    } catch (err) {
      console.error('Relearn failed:', err);
      setIsRelearning(false);
    }
  };

  const handleRecrawlUrl = async (url: string, fileId: string) => {
    setRecrawlingFileId(fileId);
    try {
      await accessDBApi.post(`/v1/domains/${id}/crawl/batch`, { urls: [url] });
      await refetchDomain();
      // ポーリング開始（対象ファイルの学習完了まで）
      const pollId = setInterval(async () => {
        const res = await refetchDomain();
        const files = res.data?.data?.crawl_files ?? [];
        const target = files.find((f: any) => f.file_id === fileId);
        if (target && (target.sync_status === 'synced' || target.sync_status === 'error')) {
          clearInterval(pollId);
          setRecrawlingFileId(null);
        }
      }, 5000);
    } catch (err) {
      console.error('Recrawl failed:', err);
      setRecrawlingFileId(null);
    }
  };

  if (!id) return null;

  const StatusBadge = ({ f }: { f: any }) => {
    if (f.sync_status === 'synced') {
      return <span style={{ padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, background: '#DCFCE7', color: '#166534', flexShrink: 0 }}
        data-testid={`url-status-${f.file_id}`} data-status={f.sync_status}>学習済み</span>;
    }
    if (f.sync_status === 'syncing' || f.sync_status === 'pending') {
      return <span style={{ padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, background: '#FEF3C7', color: '#92400E', flexShrink: 0 }}
        data-testid={`url-status-${f.file_id}`} data-status={f.sync_status}>学習中…</span>;
    }
    if (f.sync_status === 'error') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
          <span style={{ padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, background: '#FEE2E2', color: '#DC2626', flexShrink: 0 }}
            data-testid={`url-status-${f.file_id}`} data-status={f.sync_status}>学習エラー</span>
          {f.error_message && (
            <span style={{ fontSize: '10px', color: '#94A3B8', maxWidth: '200px', textAlign: 'right' }}>{f.error_message}</span>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ maxWidth: '720px', margin: '0 auto', padding: '0 16px' }}>
      {/* 戻り導線 */}
      <button
        onClick={() => navigate(`${getDomainDetailPath(id)}?tab=settings`)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#64748B', background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 16px 0' }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" x2="5" y1="12" y2="12"/><polyline points="12 19 5 12 12 5"/>
        </svg>
        設定に戻る
      </button>

      {/* page-head */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ fontSize: '22px', fontWeight: 700, color: '#0F172A', marginBottom: '6px' }}>
          学習させるページを選ぶ
        </div>
        <div style={{ fontSize: '14px', color: '#64748B', lineHeight: '1.7' }}>
          AIに覚えさせるページを選び直したり、新しいURLの追加・PDFやテキストファイルのアップロードで学習内容を更新できます。チェックを外したページは回答に使われません。
        </div>
      </div>

      {/* タブ切り替え */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
        <button
          onClick={() => setActiveTab('url')}
          style={{
            padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 700, border: 'none', cursor: 'pointer',
            background: activeTab === 'url' ? '#0a7a72' : '#F1F5F9',
            color: activeTab === 'url' ? '#fff' : '#475569',
          }}
        >
          URLを追加
        </button>
        <button
          data-testid="file-upload-tab"
          onClick={() => setActiveTab('file')}
          style={{
            padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 700, border: 'none', cursor: 'pointer',
            background: activeTab === 'file' ? '#0a7a72' : '#F1F5F9',
            color: activeTab === 'file' ? '#fff' : '#475569',
          }}
        >
          ファイルを追加
        </button>
      </div>

      {/* URL追加カード */}
      {activeTab === 'url' && (
        <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'stretch' }}>
            <input
              value={addUrl}
              onChange={e => setAddUrl(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAddUrl(); }}
              placeholder="https://example.co.jp"
              style={{ flex: 1, fontSize: '14px', padding: '11px 14px', border: '1px solid #E2E8F0', borderRadius: '8px', outline: 'none', fontFamily: 'inherit', color: '#0F172A', background: '#fff' }}
            />
            <button
              onClick={handleAddUrl}
              disabled={isAdding || !addUrl.trim()}
              style={{ background: isAdding || !addUrl.trim() ? '#94A3B8' : '#0a7a72', color: '#fff', border: 'none', borderRadius: '8px', padding: '0 18px', fontSize: '14px', fontWeight: 700, cursor: isAdding || !addUrl.trim() ? 'default' : 'pointer', flexShrink: 0 }}
            >
              {isAdding ? '追加中…' : '追加して学習'}
            </button>
          </div>
        </div>
      )}

      {/* ファイル追加カード */}
      {activeTab === 'file' && (
        <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
          {/* ドロップゾーン */}
          <div
            data-testid="file-drop-zone"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${isDragOver ? '#0a7a72' : '#CBD5E1'}`,
              borderRadius: '10px',
              padding: '28px 20px',
              textAlign: 'center',
              cursor: 'pointer',
              background: isDragOver ? '#F0FDF9' : '#F8FAFC',
              marginBottom: '12px',
              transition: 'all 0.15s',
            }}
          >
            <input
              ref={fileInputRef}
              data-testid="file-learn-input"
              type="file"
              multiple
              accept=".pdf,.txt"
              style={{ display: 'none' }}
              onChange={handleFileInputChange}
            />
            <div style={{ fontSize: '28px', marginBottom: '8px' }}>📄</div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#0F172A', marginBottom: '4px' }}>
              {isDragOver ? 'ファイルをドロップ' : 'クリックまたはドラッグ&ドロップ'}
            </div>
            <div style={{ fontSize: '12px', color: '#64748B' }}>
              対応形式: PDF / .txt（1ファイル最大10MB）
            </div>
          </div>

          {/* バリデーションエラー */}
          {validationError && (
            <div
              data-testid="file-validation-error"
              role="alert"
              style={{ fontSize: '12px', color: '#DC2626', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', padding: '8px 12px', marginBottom: '10px' }}
            >
              {validationError}
            </div>
          )}

          {/* ストレージ上限エラー */}
          {learnStorageError && (
            <div
              data-testid="storage-limit-message"
              style={{ fontSize: '12px', color: '#92400E', background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: '8px', padding: '8px 12px', marginBottom: '10px' }}
            >
              {learnStorageError}
              {' '}
              <Link to="/subscription" data-testid="upgrade-link" style={{ color: '#0a7a72', textDecoration: 'underline' }}>
                プランをアップグレード
              </Link>
            </div>
          )}

          {/* 選択済みファイル一覧 */}
          {selectedFiles.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              {selectedFiles.map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid #F1F5F9' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.file.name}
                    </div>
                    {item.progress > 0 && !item.done && !item.error && (
                      <div style={{ marginTop: '4px' }}>
                        <div style={{ height: '4px', background: '#E2E8F0', borderRadius: '2px' }}>
                          <div style={{ height: '100%', width: `${item.progress}%`, background: '#0a7a72', borderRadius: '2px', transition: 'width 0.2s' }} />
                        </div>
                        <div style={{ fontSize: '11px', color: '#64748B', marginTop: '2px' }}>{item.progress}%</div>
                      </div>
                    )}
                    {item.error && (
                      <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>{item.error}</div>
                    )}
                    {item.rejectedReason && (
                      <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>学習拒否: {item.rejectedReason}</div>
                    )}
                    {item.done && (
                      <div style={{ fontSize: '11px', color: '#059669', marginTop: '2px' }}>学習キュー投入済み</div>
                    )}
                  </div>
                  {!isLearning && (
                    <button
                      onClick={() => removeSelectedFile(i)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: '16px', padding: '2px 6px' }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* 学習開始ボタン */}
          <button
            data-testid="file-learn-submit"
            onClick={handleLearnFiles}
            disabled={selectedFiles.length === 0 || isLearning}
            style={{
              width: '100%', background: (selectedFiles.length === 0 || isLearning) ? '#94A3B8' : '#0a7a72',
              color: '#fff', border: 'none', borderRadius: '8px', padding: '12px 0', fontSize: '14px', fontWeight: 700,
              cursor: (selectedFiles.length === 0 || isLearning) ? 'default' : 'pointer',
            }}
          >
            {isLearning ? '学習中…' : `${selectedFiles.length}件を学習させる`}
          </button>
        </div>
      )}

      {/* 学習中ページ一覧カード */}
      <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '12px', overflow: 'hidden' }}>
        {/* ヘッダ行 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #E2E8F0' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: '#0F172A' }}>
            学習中のコンテンツ {crawlFiles.length}件
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={() => setShowRelearnConfirm(true)}
              disabled={isRelearning || urlFiles.length === 0}
              style={{ background: '#fff', color: '#0a7a72', border: '1.5px solid #0a7a72', borderRadius: '8px',
                       padding: '8px 16px', fontSize: '13px', fontWeight: 700,
                       cursor: (isRelearning || urlFiles.length === 0) ? 'default' : 'pointer',
                       opacity: (isRelearning || urlFiles.length === 0) ? 0.4 : 1 }}
            >
              {isRelearning ? '再学習中...' : '再学習'}
            </button>
          </div>
        </div>

        {/* コンテンツ行リスト */}
        {crawlFiles.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: '#94A3B8', fontSize: '13px' }}>
            学習中のコンテンツがありません
          </div>
        ) : (
          <div>
            {/* URL crawl files */}
            {urlFiles.map((f, i) => {
              const url = f.url ?? '';
              let displayName = url;
              let pathPart = '';
              try {
                const parsed = new URL(url.startsWith('http') ? url : `https://${url}`);
                displayName = parsed.hostname + (parsed.pathname !== '/' ? parsed.pathname : '');
                pathPart = parsed.pathname;
              } catch { /* keep original */ }

              return (
                <div
                  key={`crawl-${i}`}
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 20px', borderTop: '1px solid #F1F5F9' }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {displayName}
                    </div>
                    <div style={{ fontSize: '12px', color: '#94A3B8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {pathPart || '/'}
                    </div>
                  </div>
                  <StatusBadge f={f} />
                  {f.file_id && f.url && (f.sync_status === 'synced' || f.sync_status === 'error') && (
                    <button
                      onClick={() => handleRecrawlUrl(f.url!, f.file_id!)}
                      disabled={recrawlingFileId === f.file_id}
                      style={{
                        background: 'none', border: 'none', cursor: recrawlingFileId === f.file_id ? 'default' : 'pointer',
                        color: recrawlingFileId === f.file_id ? '#CBD5E1' : '#0a7a72',
                        fontSize: '14px', padding: '2px 6px', flexShrink: 0
                      }}
                      title="再学習"
                      data-testid={`url-recrawl-${f.file_id}`}
                    >
                      ↻
                    </button>
                  )}
                  {f.file_id && (
                    <button
                      onClick={() => setConfirmDeleteFile({ label: displayName, file_id: f.file_id! })}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: '14px', padding: '2px 6px', flexShrink: 0 }}
                      title="削除"
                      data-testid={`url-delete-${f.file_id}`}
                    >
                      🗑
                    </button>
                  )}
                </div>
              );
            })}

            {/* Upload files */}
            {uploadedFiles.map((f, i) => (
              <div
                key={`upload-${i}`}
                style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 20px', borderTop: '1px solid #F1F5F9' }}
              >
                <div style={{ width: '16px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: '#94A3B8' }}>
                  📄
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {f.original_filename ?? f.file_id}
                  </div>
                  <div style={{ fontSize: '12px', color: '#94A3B8' }}>ファイル</div>
                </div>
                <StatusBadge f={f} />
                <button
                  onClick={() => setConfirmDeleteFile({ label: f.original_filename ?? 'ファイル', file_id: f.file_id! })}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: '14px', padding: '2px 6px', flexShrink: 0 }}
                  title="削除"
                  data-testid={`url-delete-${f.file_id}`}
                >
                  🗑
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 再学習 URL選択オーバーレイ（大量URL時） */}
      {showRelearnSelect && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '32px', maxWidth: '680px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#0F172A', marginBottom: '8px' }}>学習するページを選んでください</h2>
            <p style={{ fontSize: '14px', color: '#64748B', marginBottom: '20px' }}>
              {relearnUrls.length}件のページが見つかりました。学習させたいページを選んでください。
            </p>
            <UrlDiscovery
              domainId={id!}
              domainUrl={domain?.domain ?? ''}
              initialUrls={relearnUrls.map(u => ({ url: u.url, source: u.source as 'sitemap' | 'crawl' }))}
              onComplete={() => { setShowRelearnSelect(false); refetchDomain(); }}
              onSkip={() => setShowRelearnSelect(false)}
            />
          </div>
        </div>
      )}

      {/* 再学習確認ダイアログ */}
      {showRelearnConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '32px', maxWidth: '440px', width: '100%', textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 700, marginBottom: '12px' }}>再学習しますか？</div>
            <div style={{ fontSize: '14px', color: '#64748B', marginBottom: '24px' }}>
              既存の学習済みURLは全て削除され、ベースURL（{domain?.domain}）から再学習します。アップロードしたファイルは残ります。
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button onClick={() => setShowRelearnConfirm(false)} style={{ background: '#fff', color: '#64748B', border: '1.5px solid #e2e8f0', borderRadius: '8px', padding: '10px 20px', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}>キャンセル</button>
              <button onClick={handleRelearn} style={{ background: '#0a7a72', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}>再学習する</button>
            </div>
          </div>
        </div>
      )}

      {/* 削除確認モーダル */}
      {confirmDeleteFile && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '32px', maxWidth: '420px', width: '90%' }}>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A', marginBottom: '12px' }}>
              学習から外しますか？
            </div>
            <div style={{ fontSize: '14px', color: '#64748B', lineHeight: '1.7', marginBottom: '24px' }}>
              回答に使われなくなります。元に戻すには再学習が必要です。
            </div>
            <div style={{ fontSize: '13px', color: '#94A3B8', marginBottom: '20px', wordBreak: 'break-all' }}>
              {confirmDeleteFile.label}
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmDeleteFile(null)}
                style={{ background: '#fff', color: '#475569', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '10px 20px', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}
              >
                キャンセル
              </button>
              <button
                onClick={handleConfirmDelete}
                style={{ background: '#DC2626', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}
              >
                外す
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
